import { type LocalDate } from '@js-joda/core';
import React from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '#/shared/components/Button';
import { FormControlTextArea } from '#/shared/components/FormControl';
import { Modal, ModalContent } from '#/shared/components/Modal';
import { usePaidBreakExceptionStore } from '#/shared/stores/paidBreakExceptionStore';

export interface RejectionNoteModalProps {
  shiftStartAt: LocalDate;
  isOpen: boolean;
  onClose: () => void;
}

export type RejectionNoteFormValues = {
  rejectionNote?: string;
};

export function RejectionNoteModal({ shiftStartAt, onClose, isOpen }: Readonly<RejectionNoteModalProps>) {
  const getException = usePaidBreakExceptionStore(s => s.getException);
  const setExceptionNotes = usePaidBreakExceptionStore(s => s.setExceptionNotes);

  const { handleSubmit, control, reset } = useForm<RejectionNoteFormValues>({
    defaultValues: {
      rejectionNote: getException(shiftStartAt)?.approverNotes || '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = async (formData: RejectionNoteFormValues) => {
    setExceptionNotes(shiftStartAt, formData.rejectionNote);
    onClose();
  };

  React.useEffect(() => {
    if (isOpen) {
      const exception = getException(shiftStartAt);
      reset({
        rejectionNote: exception?.approverNotes || '',
      });
    }
  }, [isOpen, reset, getException, shiftStartAt]);

  return (
    <Modal isOpen={isOpen}>
      <ModalContent title={'Add rejection note'} onCancel={onClose} onSubmit={handleSubmit(onSubmit)}>
        {/* "overflow-y-auto" so the middle section scrolls */}
        {/* "pb-4" for focus ring to be not to cut off */}
        <div className="px-24 pb-4 flex flex-col gap-16 overflow-y-auto">
          <p>Enter notes to be included on the timesheet, visible to the doctor and Medrecruit staff.</p>

          <FormControlTextArea
            label="Rejection note (optional)"
            placeholder="Enter text"
            name={'rejectionNote'}
            control={control}
            maxLength={255}
            rules={{
              maxLength: { value: 255, message: 'Maximum allowed characters is 255' },
            }}
            errorLeadingIconName={'error'}
          />
        </div>

        <div className="px-24 flex gap-12">
          <Button variant="primaryOutline" size="large" onPress={onClose} className="w-full">
            Cancel
          </Button>
          <Button type="submit" size="large" className="w-full">
            Save
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
